"use client";

import { useCallback, useMemo } from "react";
import { useSession } from "next-auth/react";
import { ROUTE_POLICIES } from "@/lib/security/route-policies";
import { evaluatePermission } from "@/lib/security/permission-engine";
import { functionality } from "@/prisma/generated/enums";
import { GRP_ROLE_ADMIN } from "@/lib/grp";

type PermissionContext = {
  userId: string;
  roles: number[];
  functionalities: functionality[];
  isAdmin: boolean;
};

export function useMenuPermissions() {
  const { data: session, status } = useSession();

  const ctx: PermissionContext | null = useMemo(() => {
    if (status !== "authenticated" || !session?.user?.id) return null;

    return {
      userId: session.user.id,
      roles: session.user.grpRoles ?? [],
      functionalities: session.user.functionalities ?? [],
      isAdmin: session.user.grpRoles?.includes(GRP_ROLE_ADMIN) ?? false,
    };
  }, [session, status]);

  const isAllowed = useCallback(
    (path: string) => {
      if (!ctx) return false;

      const policy = ROUTE_POLICIES.find((p) =>
        [...(p.page ?? [])].some((r) => r.test(path)),
      );

      if (!policy) return false;

      return evaluatePermission(ctx, policy);
    },
    [ctx],
  );

  return {
    isAllowed,
    isLoading: status === "loading",
  };
}
